import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { NetbirdUser, NetbirdUsers } from '../../interfaces/Netbird';
import { UsersService } from '../../services/users/Users';

const service = new UsersService();

interface NetbirdUsersState {
  users: NetbirdUsers;
  status: 'idle' | 'loading' | 'failed';
  error: string | null;
  vpnUsersStatus: {
    blocked: number;
    active: number;
    approvalPending: number;
  } | null;
}

const initialState: NetbirdUsersState = {
  users: [],
  status: 'idle',
  error: null,
  vpnUsersStatus: null,
};

export const fetchVpnUsers = createAsyncThunk(
  'netbirdUsers/fetchVpnUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await service.getVpnUsers();
      return response.vpnUsers || [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const approveVpnUser = createAsyncThunk(
  'netbirdUsers/approveVpnUser',
  async (
    { userId, companyId }: { userId: string; companyId: number },
    { rejectWithValue }
  ) => {
    try {
      await service.approveVpnUser(userId, companyId);
      return userId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const handleUserStatus = (users: NetbirdUser[]) => {
  return users.reduce(
    (acc, user) => {
      if (user.pending_approval) acc.approvalPending++;
      if (user.status === 'blocked' && !user.pending_approval) {
        acc.blocked++;
      }
      if (user.status === 'active') acc.active++;
      return acc;
    },
    {
      blocked: 0,
      active: 0,
      approvalPending: 0,
    }
  );
};

const netbirdUsersSlice = createSlice({
  name: 'netbirdUsers',
  initialState,
  reducers: {
    setVpnUsers: (state, action: PayloadAction<NetbirdUsers>) => {
      state.users = action.payload;
      state.vpnUsersStatus = handleUserStatus(action.payload);
    },
    updateVpnUser: (state, action: PayloadAction<NetbirdUser>) => {
      const index = state.users.findIndex((u) => u.id === action.payload.id);
      if (index !== -1) {
        state.users[index] = action.payload;
        state.vpnUsersStatus = handleUserStatus(state.users);
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVpnUsers.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(
        fetchVpnUsers.fulfilled,
        (state, action: PayloadAction<NetbirdUsers>) => {
          state.status = 'idle';
          state.users = action.payload;
          state.vpnUsersStatus = handleUserStatus(action.payload);
        }
      )
      .addCase(fetchVpnUsers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      .addCase(approveVpnUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(approveVpnUser.fulfilled, (state, action) => {
        state.status = 'idle';
        const user = state.users.find((u) => u.id === action.payload);
        if (user) {
          user.pending_approval = false;
          user.status = 'active';
          state.vpnUsersStatus = handleUserStatus(state.users);
        }
      })
      .addCase(updateVpnUserAsync.fulfilled, (state, action) => {
        state.status = 'idle';

        const user = state.users.find((u) => u.id === action.payload.userId);

        if (user) {
          user.status = action.payload.data.is_blocked ? 'blocked' : 'active';

          user.role = action.payload.data.role;
          user.auto_groups = action.payload.data.auto_groups;

          state.vpnUsersStatus = handleUserStatus(state.users);
        }
      })
      .addCase(approveVpnUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });
  },
});

export const updateVpnUserAsync = createAsyncThunk(
  'netbirdUsers/updateVpnUserAsync',
  async (
    {
      userId,
      companyId,
      data,
    }: {
      userId: string;
      companyId: number;
      data: {
        is_blocked: boolean;
        role: string;
        auto_groups: string[];
      };
    },
    { rejectWithValue }
  ) => {
    try {
      await service.updateVpnUser(userId, companyId, data);

      return {
        userId,
        data,
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const { setVpnUsers, updateVpnUser, clearError } =
  netbirdUsersSlice.actions;
export default netbirdUsersSlice.reducer;
